import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePublicSiteSettings } from '@/hooks/usePublicSiteSettings';
import { useFeaturedProperties } from '@/hooks/useProperties';
import { useTestimonials } from '@/hooks/useTestimonials';
import { PropertyCard } from '@/components/properties/PropertyCard';
import { AnimatedStats } from '@/components/home/AnimatedStats';
import { QuoteBanner } from '@/components/home/QuoteBanner';
import { DiferenciaisSection } from '@/components/home/DiferenciaisSection';
import { ContactSection } from '@/components/home/ContactSection';
import { TestimonialHero } from '@/components/home/TestimonialHero';
import { formatWhatsAppLink } from '@/lib/format';
import { AnimatedSection, StaggeredContainer } from '@/components/ui/animated-section';
import { SEO } from '@/components/seo/SEO';
export default function HomePage() {
  const {
    data: settings
  } = usePublicSiteSettings();
  const {
    data: featuredProperties
  } = useFeaturedProperties(3);
  const {
    data: testimonials
  } = useTestimonials();
  const firstTestimonial = testimonials?.[0];
  return <>
      <SEO
        title="GC Imóveis em Campinas e Paulínia | Guilherme Cavalheiro"
        description="Consultoria imobiliária em Campinas, Paulínia e região. Compra, venda e locação de imóveis com atendimento direto e estratégico."
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'RealEstateAgent',
          name: 'GC Imóveis - Guilherme Cavalheiro',
          url: 'https://www.guilhermecavalheiro.com.br/',
          areaServed: [
            { '@type': 'City', name: 'Campinas' },
            { '@type': 'City', name: 'Paulínia' }
          ],
          address: { '@type': 'PostalAddress', addressRegion: 'SP', addressCountry: 'BR' }
        }}
      />
      {/* Hero Section */}
      <section className="relative flex min-h-[70vh] md:min-h-[80vh] items-center justify-center pt-16 md:pt-20 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center animate-scale-in" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80)',
        animationDuration: '1.2s'
      }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70" />
        
        <div className="container-custom relative z-10 py-12 md:py-20 text-center px-6 md:px-4">
          <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight mb-6 md:mb-8 text-white opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
            O imóvel certo para o<br />seu momento de vida.
          </h1>
          <p className="mx-auto mb-8 md:mb-10 max-w-2xl text-base md:text-lg text-gray-300 opacity-0 animate-fade-in-up leading-relaxed" style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}>
            <span className="sm:hidden">Curadoria imobiliária com atendimento<br />próximo, visão de mercado e foco total no<br />que faz sentido pra você.</span>
            <span className="hidden sm:inline">Curadoria imobiliária com atendimento próximo, visão de mercado<br className="hidden lg:block" /> e foco total no que faz sentido pra você.</span>
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 md:gap-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '600ms', animationFillMode: 'forwards' }}>
            <a href={formatWhatsAppLink(settings?.whatsapp || '', 'Olá, quero receber oportunidades de imóveis e lançamentos.')} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto sm:min-w-[180px] border-2 border-white bg-transparent text-white transition-all duration-300 hover:border-primary hover:bg-primary hover:text-primary-foreground hover:scale-105">
                Falar no WhatsApp
              </Button>
            </a>
            <a href="#imoveis-destaque" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto sm:min-w-[180px] border-2 border-white bg-transparent text-white transition-all duration-300 hover:border-primary hover:bg-primary hover:text-primary-foreground hover:scale-105">
                Ver oportunidades
              </Button>
            </a>
          </div>
          <p className="mt-5 md:mt-6 text-xs md:text-sm text-gray-400 opacity-0 animate-fade-in" style={{ animationDelay: '800ms', animationFillMode: 'forwards' }}>
            Resposta rápida em horário comercial.
          </p>
        </div>
      </section>

      {/* About Section - Hidden temporarily */}
      {/* <section id="consultor" className="section-padding bg-background">
        <div className="container-custom">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <p className="mb-2 text-sm font-medium uppercase tracking-wider text-primary">
                {settings?.creci ? `CRECI ${settings.creci}` : 'Consultor Imobiliário'}
              </p>
              <h2 className="heading-section mb-6 text-foreground">
                Seu corretor, do primeiro contato à entrega das chaves
              </h2>
              <p className="mb-8 text-muted-foreground leading-relaxed">
                Mais do que apresentar imóveis, eu ajudo você a tomar uma decisão segura. Com visão estratégica e acompanhamento completo, o processo fica leve, transparente e bem orientado do início ao fim.
              </p>
              <div className="mb-8 grid grid-cols-2 gap-4">
                {['Curadoria personalizada de imóveis', 'Negociação com clareza e estratégia', 'Leitura de mercado e oportunidades reais', 'Acompanhamento completo até o fechamento'].map((item, i) => <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span className="text-sm text-foreground">{item}</span>
                  </div>)}
              </div>
              <a href={formatWhatsAppLink(settings?.whatsapp || '', 'Olá, quero falar com um corretor e receber opções de imóveis.')} target="_blank" rel="noopener noreferrer">
                <Button className="gap-2">
                  FALAR NO WHATSAPP <ArrowRight className="h-4 w-4" />
                </Button>
              </a>
            </div>
            <div className="relative">
              <img src={settings?.broker_photo_url || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&q=80'} alt={settings?.broker_name || 'Corretor'} className="aspect-[3/4] w-full rounded-lg object-cover" />
            </div>
          </div>
        </div>
      </section> */}

      {/* Animated Stats Section */}
      <AnimatedStats />

      {/* Diferenciais Section */}
      <DiferenciaisSection />

      {/* Quote Banner */}
      <QuoteBanner quote="Confiança se constrói com resultado, transparência e boas decisões." className="md:whitespace-nowrap" />

      {/* Featured Properties */}
      {featuredProperties && featuredProperties.length > 0 && <section id="imoveis-destaque" className="py-12 md:py-16 lg:py-24 bg-background">
          <div className="container-custom px-4 sm:px-6 lg:px-8">
            <AnimatedSection className="mb-8 md:mb-12 text-center">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4 text-foreground">
                Oportunidades selecionadas<br className="hidden sm:block" />
                <span className="sm:hidden"> </span>para morar ou investir.
              </h2>
              <div className="mx-auto mb-4 md:mb-6 h-1 w-12 md:w-16 bg-primary" />
              <p className="mx-auto max-w-2xl text-sm md:text-base text-muted-foreground px-2">
                <span className="sm:hidden">Imóveis escolhidos por localização,<br />potencial e excelente custo-benefício.</span>
                <span className="hidden sm:inline">Imóveis escolhidos por localização, potencial e excelente custo-benefício.</span>
              </p>
            </AnimatedSection>
            <StaggeredContainer 
              className="grid gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
              staggerDelay={150}
              animation="fade-in-up"
            >
              {featuredProperties.map(property => <PropertyCard key={property.id} property={property} />)}
            </StaggeredContainer>
            <AnimatedSection className="mt-8 md:mt-10 flex justify-center" delay={300}>
              <Link to="/imoveis">
                <Button variant="outline" className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 hover:scale-105">
                  VER TODOS OS IMÓVEIS <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </AnimatedSection>
          </div>
        </section>}

      {/* Testimonial Hero */}
      {firstTestimonial && <TestimonialHero quote={firstTestimonial.testimonial} author={firstTestimonial.client_name} role="Cliente" backgroundImage="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80" />}

      {/* Contact Section */}
      <ContactSection />
    </>;
}